import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Descendant } from 'slate';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchImageFromS3AndConvertToBase64 } from '../../utils/s3';
import { regenerateIcon } from '../../assets/hostedassets';
import { TIME_DATE_FORMAT } from '../../utils/constants/commonConstants';
import { closeCommentsThread, resetAndHideCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import { getSubdomain } from '../../utils/utils';
import { Mixpanel } from '../../utils/mixpanel';
import { toast } from 'react-toastify';

import { Button, Modal } from '../_custom';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import TextEditor from '../TextEditor/TextEditor';
import ReactPdfDocument from '../TextEditor/print/ReactPdfDocument';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';
import { processImagesBeforeSave, collectDocIdsFromSlate, resolveDocIdsForPdf } from '../TextEditor/plugins/imageUploadService';

import styles from './DocumentEditor.module.scss';
import { pdf } from '@react-pdf/renderer';

interface DocumentEditorProps {
    source: string;
    openModal: boolean;
    orgId: string;
    orgName: string;
    generating: boolean;
    editorInitialData: any;
    editorReady: boolean;
    savingData: boolean;
    saveDoc: (content: Descendant[]) => void;
    setShowRegenConfirmModal: (show: boolean) => void;
    userInfo: any;
    editorType: string;
    documentType: 'onePager' | 'engagementLetter' | 'bureauKycConsent' | 'nda';
    fileNamePrefix: string;
    commentContainerId: string;
    mixpanelPreviewEvent: string;
    mixpanelSaveEvent: string;
}

function DocumentEditor({
    source,
    openModal,
    orgId,
    orgName,
    generating,
    editorInitialData,
    editorReady,
    savingData,
    saveDoc,
    setShowRegenConfirmModal,
    userInfo,
    editorType,
    documentType,
    fileNamePrefix,
    commentContainerId,
    mixpanelPreviewEvent,
    mixpanelSaveEvent,
}: DocumentEditorProps) {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const contentToPrint = useRef(null);
    const editorContent = useRef<Descendant[]>([]);
    const originalDocIdsRef = useRef<string[]>(collectDocIdsFromSlate(editorInitialData));
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const mainImageUrl = domainMetadata?.mainImageUrl;
    const [triggerSave, setTriggerSave] = useState(false);
    const user = useAppSelector((state) => state.user.userData);
    const [logoBase64, setLogoBase64] = useState<string | undefined>(undefined);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
    }, []);

    useEffect(() => {
        if (triggerSave) {
            setTriggerSave(false);
        }
    }, [triggerSave, editorContent, orgId]);

    useEffect(() => {
        const loadLogo = async () => {
            if (mainImageUrl) {
                try {
                    const response = await fetchImageFromS3AndConvertToBase64(mainImageUrl);
                    if (response) {
                        setLogoBase64(response as string);
                    }
                } catch (err) {
                    // fail silently
                }
            }
        };
        loadLogo();
    }, [mainImageUrl]);

    const handlePreviewClick = async (e) => {
        e.preventDefault();
        setLoadingPreview(true);

        Mixpanel.track(mixpanelPreviewEvent, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
        });

        try {
            const rawContent = editorContent.current?.length ? editorContent.current : editorInitialData ?? [];
            const sanitized = sanitizeSlateValue(rawContent);

            if (!sanitized || sanitized.length === 0) {
                toast.error('No content to preview');
                setLoadingPreview(false);
                return;
            }

            const content = await resolveDocIdsForPdf(sanitized);
            const pdfDocument = (
                <ReactPdfDocument
                    value={content}
                    pageSize="A3"
                    logoBase64={logoBase64}
                    orgName={orgName}
                    companyName={orgName ?? 'Company'}
                    includeWatermark={false}
                    showFooter={false}
                    documentType={documentType}
                />
            );

            const blob = await pdf(pdfDocument).toBlob();
            const url = URL.createObjectURL(blob);
            const newTab = window.open(url, '_blank');

            if (!newTab) {
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.click();
            }

            setTimeout(() => URL.revokeObjectURL(url), 120000);
        } catch (err) {
            toast.error('Error generating PDF preview');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleClose = async () => {
        dispatch(closeCommentsThread());
        setTriggerSave(true);
        const processedContent = await processImagesBeforeSave(editorContent.current, originalDocIdsRef.current, documentType);
        originalDocIdsRef.current = collectDocIdsFromSlate(processedContent);
        saveDoc(processedContent);
    };

    const userMessage = useMemo(() => {
        if (!userInfo?.userName || !userInfo?.onePagerData?.lastUpdatedAt) {
            return '';
        }
        const formattedDate = dayjs(userInfo.onePagerData.lastUpdatedAt).format(TIME_DATE_FORMAT);
        return `Last edited on ${formattedDate} by ${userInfo.userName}`;
    }, [userInfo]);

    const handleSaveAndClose = async (e) => {
        e.preventDefault();
        setSaving(true);
        setTriggerSave(true);
        dispatch(closeCommentsThread());
        const processedContent = await processImagesBeforeSave(editorContent.current, originalDocIdsRef.current, documentType);
        originalDocIdsRef.current = collectDocIdsFromSlate(processedContent);
        saveDoc(processedContent);
        downloadPDF(orgName);

        Mixpanel.track(mixpanelSaveEvent, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
        });
    };

    const downloadPDF = async (companyName: string) => {
        try {
            const rawContent =
                editorContent.current?.length > 0
                    ? editorContent.current
                    : editorInitialData?.length
                    ? editorInitialData
                    : [{ type: 'paragraph' as const, children: [{ text: '' }] }];
            const content = await resolveDocIdsForPdf(sanitizeSlateValue(rawContent));

            const currentOrgName = companyName || orgName || 'Company';
            const finalFileName = currentOrgName.replace(/[^a-zA-Z0-9]/g, '_');
            const fileName = `${fileNamePrefix}_${finalFileName}.pdf`;

            const pdfDocument = (
                <ReactPdfDocument
                    value={content}
                    pageSize="A3"
                    logoBase64={logoBase64}
                    orgName={orgName}
                    companyName={currentOrgName}
                    includeWatermark={false}
                    showFooter={false}
                    documentType={documentType}
                />
            );

            const blob = await pdf(pdfDocument).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 60000);

            setSaving(false);
        } catch (err) {
            toast.error('Error generating PDF');
            setSaving(false);
        }
    };

    const renderRegenerateButton = () => (
        <div
            className={styles.Regen}
            onClick={() => {
                !generating && setShowRegenConfirmModal(true);
            }}
        >
            {generating ? (
                <>
                    <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                    <span>Regenerating</span>
                </>
            ) : (
                <>
                    <img src={regenerateIcon} alt={'re'} height={'24px'} />
                    <span>Regenerate</span>
                </>
            )}
        </div>
    );

    return openModal ? (
        <>
            <Modal
                size="full"
                open={openModal}
                style={{ padding: 0, fontSize: '12px', lineHeight: '140%', position: 'absolute' }}
                persistent
                withoutCloseButton
            >
                <div className={styles.Container}>
                    <div className={styles.Top}>
                        {!!editorReady && (
                            <TextEditor
                                source={source}
                                editorInitialData={editorInitialData}
                                targetRef={contentToPrint}
                                editorContentRef={editorContent}
                                assesseeOrgId={orgId}
                                generating={generating}
                                onClose={handleClose}
                                savingData={savingData}
                                editorType={editorType}
                            />
                        )}
                        <div className={styles.Footer}>
                            <div>{renderRegenerateButton()}</div>

                            <div className={styles.Text}>*Right-click tables to modify: add rows, add columns, or delete</div>

                            {userMessage && <div className={styles.Text}>{userMessage}</div>}

                            <div className={styles.Buttons}>
                                <Button
                                    text="Preview"
                                    variant="purpleinverted"
                                    onClick={handlePreviewClick}
                                    style={{ margin: '0 20px 0 0 ' }}
                                    loading={loadingPreview}
                                />
                                <Button
                                    text="Save & Download"
                                    variant="primary"
                                    onClick={handleSaveAndClose}
                                    style={{ marginTop: '0' }}
                                    loading={saving}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.Comments} id={commentContainerId}>
                        {/* Container Element Where comments thread is rendered via portal */}
                    </div>
                </div>
            </Modal>
        </>
    ) : (
        <></>
    );
}

export default DocumentEditor;
